import React, { useContext, useEffect, useState } from "react";
import { AuthContext } from "../../../context/AuthContext";
import BlogList from "./BlogList";
import Pagination from "./Pagination";
import RecentBlog from "./RecentBlog";
import { motion } from "motion/react";

type Props = {
  fromDetailPage?: boolean;
};

const Blogs = ({ fromDetailPage }: Props) => {
  const authContext = useContext(AuthContext);
  const [isBlogPage, setIsBlogPage] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const blogsPerPage = 6;

  useEffect(() => {
    if (window.location.pathname !== "/blogs") {
      setIsBlogPage(false);
    }

    if (!fromDetailPage) {
      const metaTag = document.querySelector("meta[name='description']");
      document.title =
        "John Bostjancic’s Blog | Finance, Risk & Business Growth";

      if (metaTag) {
        metaTag.setAttribute(
          "content",
          "Explore John Bostjancic’s blog for expert insights on finance, business strategy, risk management, and leadership."
        );
      }

      let linkTag = document.querySelector("link[rel='canonical']");
      if (linkTag) {
        linkTag.setAttribute("href", "https://johnbostjancic.co/blogs");
      } else {
        linkTag = document.createElement("link");
        (linkTag as HTMLLinkElement).rel = "canonical";
        (linkTag as HTMLLinkElement).href = "https://johnbostjancic.co/blogs";
      }

      if (!document.head.contains(linkTag)) {
        document.head.appendChild(linkTag);
      }

      //keyWords
      let keyWordTag = document.querySelector("meta[name='keywords']");
      if (!keyWordTag) {
        keyWordTag = document.createElement("meta");
        (keyWordTag as HTMLMetaElement).name = "keywords";
        document.head.appendChild(keyWordTag);
      }
      keyWordTag.setAttribute("content", "John Bostjancic Blog");
    }
  }, [isBlogPage]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  if (!authContext) {
    return null;
  }

  const indexOfLastBlog = currentPage * blogsPerPage;
  const indexOfFirstBlog = indexOfLastBlog - blogsPerPage;
  const currentBlogs = authContext.blogs.slice(
    indexOfFirstBlog,
    indexOfLastBlog
  );

  return (
    <>
      {isBlogPage && (
        <div className="max-w-screen-2xl mx-auto px-5 lg:px-20">
          <div className="lg:h-[450px] bg-[#e6e0ff] lg:bg-transparent lg:bg-bg2 bg-cover lg:bg-contain bg-no-repeat xl:bg-cover lg:-mt-20 xl:-mt-28 relative rounded-b-[50px] rounded-t-[50px] lg:rounded-t-none">
            <div className="w-full h-full flex flex-col justify-center xl:justify-end px-4 xl:px-10 pb-8 xl:pb-14 pt-8 lg:pt-0">
              <div className="flex flex-col lg:flex-row justify-between">
                <div className="lg:w-[50%]">
                  <motion.div
                    initial={{ y: -100, opacity: 0 }}
                    whileInView={{ y: 0, opacity: 1 }}
                    transition={{ duration: 0.5 }}
                    viewport={{ once: true }}
                    className="py-3 px-4 montserrat-medium text-primary bg-white rounded-full w-32 flex justify-center mb-5"
                  >
                    BLOGS
                  </motion.div>
                  <motion.h1
                    initial={{ y: -100, opacity: 0 }}
                    whileInView={{ y: 0, opacity: 1 }}
                    transition={{ duration: 0.5, delay: 0.5 }}
                    viewport={{ once: true }}
                    className="text-secondary text-4xl xl:text-5xl montserrat-semibold mb-4 lg:mb-0"
                  >
                    Insights and Strategies by{" "}
                    <span className="montserrat-semibold text-primary">
                      John Bostjancic
                    </span>{" "}
                  </motion.h1>
                </div>
                <div className="lg:w-[40%] flex items-end">
                  <motion.p
                    initial={{ y: -100, opacity: 0 }}
                    whileInView={{ y: 0, opacity: 1 }}
                    transition={{ duration: 0.5, delay: 1 }}
                    viewport={{ once: true }}
                    className="montserrat-regular text-secondary"
                  >
                    Explore the latest posts by John Bostjancic, offering
                    insights on finance, healthcare, and business strategy. Stay
                    updated with expert advice and industry trends.
                  </motion.p>
                </div>
              </div>
            </div>
            <a
              href="/contact"
              className="hidden lg:block bg-[#E9FF7A] py-3 px-7 absolute top-3 xl:top-10 right-5 xl:right-20 2xl:right-24 montserrat-medium rounded-full text-xl z-30"
            >
              Contact Me
            </a>
          </div>
        </div>
      )}
      <div className="flex flex-col gap-10 overflow-hidden mx-auto max-w-screen-2xl items-center justify-center px-4 sm:px-20 py-10 lg:py-0 xl:py-28">
        <div className="w-full">
          <div className="grid xl:grid-cols-2 gap-4 sm:gap-10 xl:gap-4">
            {isBlogPage &&
              authContext.blogs
                .slice(0, 2)
                .map((blog) => <RecentBlog key={blog.slug} blog={blog} />)}
          </div>
        </div>
        <div className="py-6 sm:py-10">
          <BlogList blogs={currentBlogs} />
          <Pagination
            currentPage={currentPage}
            totalPages={Math.ceil(authContext.blogs.length / blogsPerPage)}
            onPageChange={handlePageChange}
          />
        </div>
      </div>
    </>
  );
};

export default Blogs;
